import 'css/src/global';

import React, { useCallback, useEffect, useState } from 'react';

import { ApiService, Urls } from 'js/src/client';
import type { InvitationResponse } from 'js/src/client/types.gen';
import { AuthQueryParams } from 'js/src/components/auth/auth-consts';
import Sidebar from 'js/src/components/auth/common/Sidebar';
import Title from 'js/src/components/auth/common/Title';
import SignUpForm from 'js/src/components/auth/signup/SignUpForm';
import TermsAndPrivacy from 'js/src/components/auth/signup/TermsAndPrivacy';
import { getCurrentPathWithNext } from 'js/src/data/account/utils';
import Navigation from 'js/src/libs/navigation';
import { getQueryParam } from 'js/src/libs/querystring';

const getInviteTitleText = ({
    hasInvite = false,
    inviter = '',
    loginUrl = '',
    queryString = '',
}): [string, React.ReactNode] => {
    let title = 'Create an account';
    let description: React.ReactNode = '';

    if (hasInvite) {
        if (inviter !== '') {
            title = `You've been invited to join ${inviter}'s team`;
        } else {
            title = "You've been invited to join a team";
        }

        description = (
            <>
                <a href={loginUrl + queryString} className="fw-semibold">
                    Login
                </a>
                {' or sign up to accept the invitation'}
            </>
        );
    }

    return [title, description];
};

const PageSkeleton = React.memo(function PageSkeleton() {
    return <div className="signup-skeleton"></div>;
});

const SignUpPage = React.memo(function SignUpPage() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [invitation, setInvitation] = useState<InvitationResponse | null>(null);

    const fetchInvite = useCallback(function () {
        ApiService.authInvitationRetrieve({
            query: { uuid: getQueryParam(AuthQueryParams.INVITE_UUID)! },
        })
            .then((response) => {
                if (response.data.sso_required) {
                    if (response.data.user_exists) {
                        Navigation.goToLocation(
                            `${Urls.SSO_VERIFICATION}?${AuthQueryParams.INVITE_UUID}=${getQueryParam(AuthQueryParams.INVITE_UUID)}`,
                        );
                    } else {
                        Navigation.goToLocation(
                            `${Urls.SSO_VERIFICATION}?${AuthQueryParams.INVITE_UUID}=${getQueryParam(AuthQueryParams.INVITE_UUID)}&is_signup=1`,
                        );
                    }
                }
                setInvitation(response.data);
            })
            .catch(() => {
                // do nothing
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        getQueryParam(AuthQueryParams.INVITE_UUID) ? fetchInvite() : setIsLoading(false);
    }, [fetchInvite, setIsLoading]);

    const { sender_first_name = '', recipient_email = '' } = invitation || {};
    const queryString = window.location.search || '';
    const [title, description] = getInviteTitleText({
        hasInvite: !!getQueryParam(AuthQueryParams.INVITE_UUID),
        inviter: sender_first_name,
        loginUrl: Urls.LOGIN,
        queryString: queryString,
    });

    return (
        <div className="row">
            <div className="col-xl-4 d-none d-xl-block">
                <Sidebar />
            </div>

            <div className="col-xl-8 col-sm-12 d-flex justify-content-center">
                <div className="details-form">
                    <div className="form-sidebar vertical-flex">
                        <div className="flex-spacer">
                            {/* Spaces out top in the form on desktop */}
                            <div className=" auth-base-logo d-block d-xl-none mb-l5-48">
                                <a href="/">
                                    <img
                                        src="https://cdn.lumen5.com/lumen5-site-images/L5-logo/2024/Lumen5-PrimaryLogo.svg"
                                        alt="Lumen5"
                                        width="125"
                                    />
                                </a>
                            </div>
                        </div>
                        <div className="signup-form main-content">
                            {isLoading ? (
                                <PageSkeleton />
                            ) : (
                                <>
                                    <Title title={title} description={description} />
                                    <SignUpForm
                                        email={recipient_email}
                                        loginUrl={Urls.LOGIN}
                                        facebookAuthUrl={Urls.FACEBOOK_AUTH}
                                        googleAuthUrl={Urls.GOOGLE_AUTH}
                                        currentPath={getCurrentPathWithNext()}
                                    />
                                </>
                            )}
                        </div>
                        <TermsAndPrivacy termsUrl={Urls.TERMS} privacyUrl={Urls.PRIVACY} />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default SignUpPage;
